import { useMemo, useRef, useState } from 'react';
import { setFocusVisible } from '@front/sharedcomponents/Accessibility';
import { RowType } from '@shared/utils/Table/TableRow.interface';
import { compareRows } from '@shared/utils/Table/TableRowComparer';
function regroupRows(rows, groupColumn) {
    if (isNullOrUndefined(groupColumn)) {
        return rows;
    }
    const getValueKey = (value) => {
        if (isNullOrUndefined(value)) {
            return '';
        }
        if (value instanceof Date) {
            return value.getTime().toString();
        }
        return String(value);
    };
    const groupRowsById = new Map();
    for (const row of rows) {
        const rowValue = row.data.getValue(groupColumn.id);
        const groupId = `group:${groupColumn.id}:${getValueKey(rowValue)}`;
        let groupRow = groupRowsById.get(groupId);
        if (isNullOrUndefined(groupRow)) {
            groupRow = {
                id: groupId,
                type: RowType.GroupRow,
                children: [],
                data: {
                    getValue: (columnId) => columnId === groupColumn.id ? row.data.getValue(groupColumn.id) : '',
                    getFormattedValue: (columnId) => columnId === groupColumn.id ? row.data.getFormattedValue(groupColumn.id) : ''
                },
                disabled: false
            };
            groupRowsById.set(groupId, groupRow);
        }
        groupRow.children.push(row);
    }
    return Array.from(groupRowsById.values());
}
function resortDefaultRows(rows, sortingRules, collapsedAggregatedRowsIds) {
    // 1. Sort rows
    const sortedRows = [...rows].sort((a, b) => compareRows(a, b, sortingRules));
    // 2. Sort child rows in rows if not collapsed
    for (const row of sortedRows) {
        if (isValidArray(row.children) && !collapsedAggregatedRowsIds.has(row.id)) {
            row.children = resortDefaultRows(row.children, sortingRules, collapsedAggregatedRowsIds);
        }
    }
    return sortedRows;
}
function resortGroupingRows(rows, sortingRules, groupColumn, collapsedRowsIds) {
    // 1. Sort group rows only when sorting by the grouping column; otherwise keep group order
    const groupRows = sortingRules.columnId === groupColumn.id ? [...rows].sort((a, b) => compareRows(a, b, sortingRules)) : rows;
    // 2. Sort child rows in group rows if not collapsed
    for (const groupRow of groupRows) {
        if (collapsedRowsIds.has(groupRow.id)) {
            continue;
        }
        groupRow.children = resortDefaultRows(groupRow.children, sortingRules, collapsedRowsIds);
    }
    return groupRows;
}
function resortRows(rows, sortingRules, groupColumn, collapsedRowsIds) {
    if (isNullOrUndefined(groupColumn)) {
        return resortDefaultRows(rows, sortingRules, collapsedRowsIds);
    }
    return resortGroupingRows(rows, sortingRules, groupColumn, collapsedRowsIds);
}
function createPlainRows(rows, collapsedRowsIds, depth) {
    const plainRows = [];
    const depthByRowId = new Map();
    for (const row of rows) {
        plainRows.push(row);
        depthByRowId.set(row.id, depth);
        if (!isValidArray(row.children)) {
            continue;
        }
        if (row.type === RowType.GroupRow) {
            if (!collapsedRowsIds.has(row.id)) {
                const nested = createPlainRows(row.children, collapsedRowsIds, depth + 1);
                plainRows.push(...nested.plainRows);
                nested.depthByRowId.forEach((d, id) => depthByRowId.set(id, d));
            }
        }
        else if (!collapsedRowsIds.has(row.id)) {
            for (const child of row.children) {
                plainRows.push(child);
                depthByRowId.set(child.id, depth + 1);
            }
        }
    }
    return { plainRows, depthByRowId };
}
export function useTableRows(rows, sortingRules, groupColumn, onSelectedRowsChanged, onRowDoubleClick, onCellClick, controlledSelectedRowIds) {
    const [internalSelectedRows, setInternalSelectedRows] = useState(new Set());
    const [collapsedRowsIds, setCollapsedRowsIds] = useState(new Set());
    const selectionAnchorRef = useRef(null);
    const isControlled = controlledSelectedRowIds !== undefined;
    const selectedRows = useMemo(() => {
        if (isControlled) {
            return new Set(rows.filter((r) => controlledSelectedRowIds.includes(r.id)));
        }
        return internalSelectedRows;
    }, [isControlled, controlledSelectedRowIds, internalSelectedRows, rows]);
    const updateSelection = (action) => {
        const newSelection = typeof action === 'function' ? action(selectedRows) : action;
        if (!isControlled) {
            setInternalSelectedRows(newSelection);
        }
        onSelectedRowsChanged === null || onSelectedRowsChanged === void 0 ? void 0 : onSelectedRowsChanged(Array.from(newSelection));
    };
    const selectRowsInRange = (anchor, endIndex, visibleRowsList) => {
        const anchorIndex = visibleRowsList.findIndex((r) => r.id === anchor.id);
        if (anchorIndex === -1 || endIndex < 0 || endIndex >= visibleRowsList.length) {
            return;
        }
        const start = Math.min(anchorIndex, endIndex);
        const end = Math.max(anchorIndex, endIndex);
        const newSelectedRows = new Set();
        for (let i = start; i <= end; i++) {
            const visibleRow = visibleRowsList[i];
            if (visibleRow.type !== RowType.GroupRow) {
                newSelectedRows.add(visibleRow);
            }
        }
        updateSelection(newSelectedRows);
    };
    const { rows: visibleRows, rowsDepth } = useMemo(() => {
        //1. Regroup rows
        const groupedRows = regroupRows(rows, groupColumn);
        //2. Resort rows
        const sortedRows = resortRows(groupedRows, sortingRules, groupColumn, collapsedRowsIds);
        //3. Create plain rows array and depth map (same order as flatten)
        const { plainRows, depthByRowId } = createPlainRows(sortedRows, collapsedRowsIds, 0);
        return { rows: plainRows, rowsDepth: depthByRowId };
    }, [rows, sortingRules, groupColumn, collapsedRowsIds]);
    const handleRowToggleCollapse = (row) => {
        setCollapsedRowsIds((prev) => {
            const newCollapsedRowsIds = new Set(prev);
            if (prev.has(row.id)) {
                newCollapsedRowsIds.delete(row.id);
            }
            else {
                newCollapsedRowsIds.add(row.id);
            }
            return newCollapsedRowsIds;
        });
    };
    const handleRowSelect = (row) => {
        updateSelection((prev) => {
            if (prev.size === 1 && prev.has(row)) {
                selectionAnchorRef.current = null;
                return new Set();
            }
            else {
                selectionAnchorRef.current = row;
                return new Set([row]);
            }
        });
    };
    const handleRowNavigation = (event, row) => {
        var _a;
        const isShiftKey = event.shiftKey === true;
        const targetRow = event.target;
        const isDownDirection = event.key === 'ArrowDown';
        const nextRowElement = isDownDirection
            ? targetRow === null || targetRow === void 0 ? void 0 : targetRow.nextElementSibling
            : targetRow === null || targetRow === void 0 ? void 0 : targetRow.previousElementSibling;
        if (isNullOrUndefined(nextRowElement) === true) {
            return;
        }
        setFocusVisible(nextRowElement);
        const currentIndex = visibleRows.findIndex((r) => r.id === row.id);
        const nextIndex = isDownDirection ? currentIndex + 1 : currentIndex - 1;
        if (isShiftKey) {
            (_a = selectionAnchorRef.current) !== null && _a !== void 0 ? _a : (selectionAnchorRef.current = row);
            selectRowsInRange(selectionAnchorRef.current, nextIndex, visibleRows);
        }
        else {
            selectionAnchorRef.current = visibleRows[nextIndex];
            const nextRow = visibleRows[nextIndex];
            updateSelection(new Set([nextRow]));
        }
    };
    const handleCellNavigation = (event, column, _row) => {
        var _a;
        if (column === null) {
            const rowElement = event.currentTarget;
            if (((_a = rowElement === null || rowElement === void 0 ? void 0 : rowElement.children) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                const cellElement = rowElement.children[0];
                setFocusVisible(cellElement);
            }
        }
        else {
            const isRightDirection = event.key === 'ArrowRight';
            const cellElement = event.currentTarget;
            if (isRightDirection) {
                const nextCellElement = cellElement === null || cellElement === void 0 ? void 0 : cellElement.nextElementSibling;
                if (isNullOrUndefined(nextCellElement) === false) {
                    setFocusVisible(nextCellElement);
                }
            }
            else {
                const previousCellElement = cellElement === null || cellElement === void 0 ? void 0 : cellElement.previousElementSibling;
                if (isNullOrUndefined(previousCellElement) === false) {
                    setFocusVisible(previousCellElement);
                }
            }
        }
    };
    const handleRowMultiSelect = (row) => {
        updateSelection((prev) => {
            const newSelectedRows = new Set(prev);
            if (selectedRows.has(row)) {
                newSelectedRows.delete(row);
            }
            else {
                newSelectedRows.add(row);
            }
            return newSelectedRows;
        });
    };
    const handleRowClick = (event, row) => {
        if (event.button !== 0) {
            return;
        }
        const isGroupRow = row.type === RowType.GroupRow;
        if (isGroupRow) {
            handleRowToggleCollapse(row);
            return;
        }
        if (event.ctrlKey === true || event.metaKey === true) {
            handleRowMultiSelect(row);
        }
        else {
            handleRowSelect(row);
        }
    };
    const handleRowDoubleClick = (event, row) => {
        if (event.button !== 0) {
            return;
        }
        onRowDoubleClick === null || onRowDoubleClick === void 0 ? void 0 : onRowDoubleClick(row);
    };
    const handleRowKeyDown = (event, row) => {
        let isHandled = false;
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                handleRowNavigation(event, row);
                isHandled = true;
                break;
            case 'Enter':
                row.type === RowType.GroupRow ? handleRowToggleCollapse(row) : handleRowSelect(row);
                isHandled = true;
                break;
            case 'ArrowRight':
            case 'ArrowLeft':
                handleCellNavigation(event, null, row);
                isHandled = true;
                break;
        }
        if (isHandled) {
            event.stopPropagation();
            event.preventDefault();
        }
    };
    const handleCellClick = (event, column, row) => {
        if (event.button === 0) {
            onCellClick === null || onCellClick === void 0 ? void 0 : onCellClick(column, row);
        }
    };
    const handleCellKeyDown = (event, column, row) => {
        let isHandled = false;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowLeft':
                handleCellNavigation(event, column, row);
                isHandled = true;
                break;
            case 'ArrowUp':
            case 'ArrowDown':
                setFocusVisible(event.currentTarget.parentElement);
                isHandled = true;
                break;
        }
        if (isHandled) {
            event.stopPropagation();
            event.preventDefault();
        }
    };
    return {
        allRows: rows,
        visibleRows,
        selectedRows,
        collapsedRowsIds,
        rowsDepth,
        handleRowClick,
        handleRowDoubleClick,
        handleRowToggleCollapse,
        handleRowKeyDown,
        handleCellClick,
        handleCellKeyDown
    };
}
